import { useMemo } from "react";
import { Carousel } from "primereact/carousel";
import { Skeleton } from "primereact/skeleton";
import {
  buildResponsiveOptions,
  useCurrentNumVisible,
} from "sj-ui-toolkit/src/helpers/carouselHelpers";
import { C } from "sj-ui-toolkit/src/atlas/utils";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import type { TournamentDetails } from "sj-ui-toolkit/src/api/types/tournamentTypes";
import type { Assignment } from "sj-ui-toolkit/src/api/types/assignmentTypes";
import type { PersonalizedLessonAssignment } from "sj-ui-toolkit/src/helpers/plaHelpers";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";
import { AssessmentCard } from "../DashboardCards/AssessmentCard";
import { TournamentCard } from "../DashboardCards/TournamentCard";
import { AssignmentCard } from "../DashboardCards/AssignmentCard";
import type { FormattedAssessment } from "../../../../helpers/assessments";
import carouselStyles from "../CarouselOverrides.module.scss";

const lowestThreshold = 750;
const interval = 400;
const maxCards = 8;

// Each todo keeps its source shape and gains a `todoType` discriminant plus sort metadata.
type SortMeta = { sortDate: number | null; isPastDue: boolean };
type DatedTodo =
  | (FormattedAssessment & SortMeta & { todoType: "assessment" })
  | (TournamentDetails & SortMeta & { todoType: "tournament" })
  | (Assignment & SortMeta & { todoType: "assignment" });
type Todo =
  | DatedTodo
  | (PersonalizedLessonAssignment & {
      todoType: "pla";
      progress: { percents: number; passed: number; all: number };
    });

interface TodoCarouselProps {
  assessments?: FormattedAssessment[];
  tournaments?: TournamentDetails[];
  assignments?: Assignment[];
  personalizedLessonAssignment?: PersonalizedLessonAssignment | null;
  isLoading?: boolean;
}

export const TodoCarousel = ({
  assessments = [],
  tournaments = [],
  assignments = [],
  personalizedLessonAssignment = null,
  isLoading = false,
}: TodoCarouselProps) => {
  const { navigateTo } = useSmartNavigation();
  const carouselConfig = { lowestThreshold, maxCards, interval };
  const responsiveOptions = buildResponsiveOptions(carouselConfig);
  const currentNumVisible = useCurrentNumVisible(carouselConfig);

  // Combine and sort all todos by due date/finish date
  const combinedTodos = useMemo((): Todo[] => {
    const todos: DatedTodo[] = [];
    const now = Date.now();

    // Add assessments with type identifier
    assessments.forEach(assessment => {
      const dueTime = assessment.dueDate ? new Date(assessment.dueDate).getTime() : null;
      todos.push({
        ...assessment,
        todoType: "assessment",
        sortDate: dueTime,
        isPastDue: dueTime ? dueTime < now : false,
      });
    });

    // Add tournaments with type identifier
    tournaments.forEach(tournament => {
      const finishTime = tournament.finishDate ? new Date(tournament.finishDate).getTime() : null;
      todos.push({
        ...tournament,
        todoType: "tournament",
        sortDate: finishTime,
        isPastDue: finishTime ? finishTime < now : false,
      });
    });

    // Add assignments with type identifier
    assignments.forEach(assignment => {
      const dueTime = assignment.dueDate ? new Date(assignment.dueDate).getTime() : null;
      todos.push({
        ...assignment,
        type: assignment.type, // Preserve original type for AssignmentCard
        todoType: "assignment",
        sortDate: dueTime,
        isPastDue: dueTime ? dueTime < now : false,
      });
    });

    // Sort: upcoming items first (by date), then past due items (by date), then items without dates
    todos.sort((a, b) => {
      // Items without dates go to the end
      if (a.sortDate === null && b.sortDate === null) return 0;
      if (a.sortDate === null) return 1;
      if (b.sortDate === null) return -1;

      // If one is past due and the other isn't, upcoming comes first
      if (a.isPastDue && !b.isPastDue) return 1;
      if (!a.isPastDue && b.isPastDue) return -1;

      // Both are either past due or upcoming, sort by date
      return a.sortDate - b.sortDate;
    });

    // The dated todos are already sorted above; prepending the PLA here keeps it first
    // in the carousel, exactly like the pre-conversion `todos.unshift(...)` did.
    if (personalizedLessonAssignment) {
      return [
        {
          ...personalizedLessonAssignment,
          todoType: "pla",
          progress: {
            percents: personalizedLessonAssignment.progressPercent,
            passed: personalizedLessonAssignment.completedLessons,
            all: personalizedLessonAssignment.totalLessons,
          },
        },
        ...todos,
      ];
    }

    return todos;
  }, [assessments, tournaments, assignments, personalizedLessonAssignment]);

  if (combinedTodos.length === 0 && !isLoading) {
    return null;
  }

  // No keys needed: Carousel renders each template result inside its own keyed
  // CarouselItem wrapper, so the cards are never list children. (The old
  // key={`assessment-${index}`} keys were always "...-undefined" — PrimeReact calls
  // itemTemplate(item) with one argument.)
  const renderTodoCard = (todo: Todo) => {
    switch (todo.todoType) {
      case "assessment":
        return (
          <AssessmentCard
            {...todo}
            variation="todo"
            testId={`assessment-card-todo-${sanitizeTestId(todo.title)}`}
          />
        );
      case "tournament":
        return <TournamentCard {...todo} />;
      case "assignment":
        return <AssignmentCard {...todo} />;
      case "pla":
        return (
          <AssignmentCard
            {...todo}
            tagText="Personalized Assignment"
            onNavigate={() =>
              navigateTo(buildRoute("Learn.routes.personalizedLessonAssignment.route"))
            }
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="mt-8">
        <h2 className="atlas-h5 pb-4">TO DO</h2>
        <div className="flex gap-4">
          <Skeleton width="100%" height="350px" borderRadius="8px" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden md:block" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden lg:block" />
          <Skeleton width="100%" height="350px" borderRadius="8px" className="hidden xl:block" />
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-8 ${carouselStyles.carouselSection}`} data-testid="dashboard-todo-carousel">
      <h2 className={`atlas-h5 pb-4 ${carouselStyles.sectionHeader}`}>
        TO DO ({combinedTodos.length})
      </h2>
      <Carousel
        aria-label="TO DO"
        className={C(
          carouselStyles.customCarousel,
          combinedTodos.length <= currentNumVisible && carouselStyles.singlePageCarousel
        )}
        value={combinedTodos}
        responsiveOptions={responsiveOptions}
        numVisible={maxCards}
        numScroll={maxCards}
        showNavigators={combinedTodos.length > currentNumVisible}
        showIndicators={combinedTodos.length > currentNumVisible}
        itemTemplate={renderTodoCard}
      />
    </div>
  );
};
